import { XIcon } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useRef } from "react";

import { cn } from "../cn.js";

export interface DialogProps {
	open: boolean;
	onClose: () => void;
	title: string;
	description?: ReactNode;
	children?: ReactNode;
	footer?: ReactNode;
	/** Blocks dismissal while a destructive action is in flight. */
	dismissable?: boolean;
	size?: "sm" | "md" | "lg" | "xl";
}

const SIZES = { sm: "max-w-sm", md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-[min(96vw,1280px)]" } as const;

/**
 * Modal dialog built on the native `<dialog>` element, which gives us the
 * focus trap, the top layer and Escape handling without reimplementing them.
 */
export function Dialog({
	open,
	onClose,
	title,
	description,
	children,
	footer,
	dismissable = true,
	size = "md",
}: DialogProps) {
	const ref = useRef<HTMLDialogElement>(null);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		if (open && !element.open) element.showModal();
		else if (!open && element.open) element.close();
	}, [open]);

	useEffect(() => {
		const element = ref.current;
		if (!element) return;

		const handleCancel = (event: Event) => {
			event.preventDefault();
			if (dismissable) onClose();
		};
		element.addEventListener("cancel", handleCancel);
		return () => element.removeEventListener("cancel", handleCancel);
	}, [dismissable, onClose]);

	return (
		<dialog
			ref={ref}
			aria-labelledby="dialog-title"
			className={cn(
				"m-auto w-[calc(100vw-2rem)] rounded-(--radius-xl) border border-(--border-subtle)",
				"bg-(--surface-raised) p-0 text-(--text-primary) shadow-(--shadow-overlay)",
				"backdrop:bg-(--color-ink-950)/40",
				SIZES[size],
			)}
			// Clicking the backdrop (the dialog element itself, outside its
			// content box) dismisses, matching platform convention.
			onClick={(event) => {
				if (dismissable && event.target === ref.current) onClose();
			}}
		>
			<div className="flex items-start justify-between gap-4 px-5 pt-5">
				<div className="flex flex-col gap-1">
					<h2 id="dialog-title" className="text-base font-semibold">
						{title}
					</h2>
					{description && <p className="text-sm text-(--text-secondary)">{description}</p>}
				</div>
				{dismissable && (
					<button
						type="button"
						onClick={onClose}
						aria-label="Close"
						className="-mr-1 -mt-1 inline-flex size-8 items-center justify-center rounded-(--radius-sm) text-(--text-muted) hover:bg-(--surface-sunken) hover:text-(--text-primary)"
					>
						<XIcon className="size-4" aria-hidden="true" />
					</button>
				)}
			</div>

			{children && <div className="px-5 py-4">{children}</div>}

			{footer && (
				<div className="flex flex-wrap justify-end gap-2 border-t border-(--border-subtle) px-5 py-3.5">
					{footer}
				</div>
			)}
		</dialog>
	);
}

export interface ConfirmDialogProps extends Omit<DialogProps, "footer" | "children"> {
	/**
	 * Destructive actions require the customer to type an exact value, so a
	 * mis-click cannot delete a mailbox (spec §20.2, §67).
	 */
	confirmPhrase?: string;
	confirmValue?: string;
	onConfirmValueChange?: (value: string) => void;
	confirmLabel: string;
	onConfirm: () => void;
	pending?: boolean;
	children?: ReactNode;
}
